// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\RepairableSparePartDetail.jsx
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import ConfirmDialog from "../components/ConfirmDialog";
import { Context } from "../store/appContext";
import {
  REPAIR_CLOSE_RESULT_LABELS,
  REPAIR_COMMENT_CATEGORY_LABELS,
  REPAIR_DOCUMENT_TYPE_LABELS,
  REPAIR_STATUS_LABELS,
} from "../store/flux/repairableSparePartActions";
import RepairableSparePartForm from "./RepairableSparePartForm";
import "../styles/layout.css";
import "../styles/utilities.css";
import "../styles/forms.css";
import "../styles/repairableSpareParts.css";

const formatDate = (value, includeTime = false) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(includeTime
      ? {
          hour: "2-digit",
          minute: "2-digit",
        }
      : {}),
  }).format(date);
};

const formatMoney = (value, currency = "EUR") => {
  if (value === null || value === undefined || value === "") return "—";

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return `${value} ${currency}`;

  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: currency || "EUR",
  }).format(numeric);
};

const formatFileSize = (value) => {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const displayValue = (value) =>
  value === null || value === undefined || value === "" ? "—" : value;

const formatCaseWarning = (warning) => {
  if (typeof warning === "string") return warning;

  if (!warning || typeof warning !== "object") {
    return String(warning || "Advertencia del expediente");
  }

  const message = String(
    warning.message || warning.code || "Advertencia del expediente"
  ).trim();
  const activeCount = Number(warning.active_count);

  if (Number.isFinite(activeCount) && activeCount > 0) {
    return `${message} (${activeCount} expedientes activos)`;
  }

  return message;
};

const getDocumentExtension = (documentItem) => {
  const filename = String(documentItem?.original_filename || "").toLowerCase();
  const parts = filename.split(".");
  return parts.length > 1 ? parts.pop() : "";
};

const canPreviewDocument = (documentItem) => {
  const mimeType = String(documentItem?.mime_type || "").toLowerCase();
  const extension = getDocumentExtension(documentItem);

  return (
    mimeType.includes("pdf") ||
    mimeType.startsWith("image/") ||
    ["pdf", "jpg", "jpeg", "png"].includes(extension)
  );
};

const statusClass = (status) =>
  `repairable-status repairable-status-${String(status || "unknown")
    .toLowerCase()
    .replaceAll("_", "-")}`;

const userName = (user) =>
  user?.username || user?.email || (user?.id ? `Usuario ${user.id}` : "—");

const Modal = ({ open, title, children, onClose, footer, danger = false }) => {
  useEffect(() => {
    if (!open) return undefined;

    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.body.classList.add("modal-open");
    window.addEventListener("keydown", handleKey);

    return () => {
      document.body.classList.remove("modal-open");
      window.removeEventListener("keydown", handleKey);
    };
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div className="repairable-modal-overlay" role="presentation">
      <section
        className={`repairable-modal${danger ? " repairable-modal-danger" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="repairable-modal-header">
          <div>
            <span className="repairable-eyebrow">ACCIÓN DEL EXPEDIENTE</span>
            <h2>{title}</h2>
          </div>
          <button
            type="button"
            className="repairable-icon-button"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </header>
        <div className="repairable-modal-body">{children}</div>
        {footer && <footer className="repairable-modal-footer">{footer}</footer>}
      </section>
    </div>
  );
};

const DetailItem = ({ label, value, wide = false, children }) => (
  <div className={`repairable-detail-item${wide ? " repairable-detail-wide" : ""}`}>
    <span>{label}</span>
    {children || <strong>{displayValue(value)}</strong>}
  </div>
);

const RepairableSparePartDetail = () => {
  const { id: caseId } = useParams();
  const { store, actions } = useContext(Context);
  const role = String(store.role || localStorage.getItem("role") || "")
    .trim()
    .toLowerCase();
  const isAdmin = role === "admin";

  const [repairCase, setRepairCase] = useState(null);
  const [allowedTransitions, setAllowedTransitions] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [options, setOptions] = useState(null);
  const [economics, setEconomics] = useState(null);
  const [history, setHistory] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [comments, setComments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [secondaryLoading, setSecondaryLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);

  const [transitionOpen, setTransitionOpen] = useState(false);
  const [transitionForm, setTransitionForm] = useState({
    new_status: "",
    comment: "",
    sent_at: "",
    estimated_return_date: "",
    received_at: "",
    close_result: "",
    close_reason: "",
    cancellation_reason: "",
  });
  const [responsibleOpen, setResponsibleOpen] = useState(false);
  const [responsibleForm, setResponsibleForm] = useState({
    responsible_user_id: "",
    reason: "",
  });
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionForm, setCorrectionForm] = useState({
    field_name: "final_notes",
    new_value: "",
    reason: "",
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");

  const [commentForm, setCommentForm] = useState({
    category: "SEGUIMIENTO",
    text: "",
    is_internal: true,
  });
  const [commentLoading, setCommentLoading] = useState(false);

  const [documentForm, setDocumentForm] = useState({
    file: null,
    document_type: "DIAGNOSTICO",
    document_scope: "TECNICO",
    description: "",
  });
  const [documentLoading, setDocumentLoading] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [documentPreview, setDocumentPreview] = useState(null);

  const loadPrimary = useCallback(
    async (signal) => {
      const [caseResult, optionsResult] = await Promise.all([
        actions.getRepairableSparePart(caseId, { signal }),
        actions.getRepairableSparePartOptions({ signal }),
      ]);

      if (signal.aborted) return false;

      if (!caseResult.success) {
        setError(caseResult.error);
        return false;
      }

      const data = caseResult.data || {};
      setRepairCase(data.case || data);
      setAllowedTransitions(
        Array.isArray(data.allowed_transitions) ? data.allowed_transitions : [],
      );
      setWarnings(Array.isArray(data.warnings) ? data.warnings : []);

      if (optionsResult.success) {
        setOptions(optionsResult.data);
      }

      return true;
    },
    [actions, caseId],
  );

  const loadSecondary = useCallback(
    async (signal) => {
      setSecondaryLoading(true);

      const requests = [
        actions.getRepairableSparePartHistory(caseId, { signal }),
        actions.getRepairableSparePartComments(caseId, { signal }),
        actions.getRepairableSparePartDocuments(caseId, {}, { signal }),
      ];

      if (isAdmin) {
        requests.push(
          actions.getRepairableSparePartCorrections(caseId, { signal }),
          actions.getRepairableSparePartEconomics(caseId, { signal }),
        );
      }

      const results = await Promise.all(requests);

      if (signal.aborted) return;

      if (results[0]?.success) {
        setHistory(Array.isArray(results[0].data) ? results[0].data : []);
      }
      if (results[1]?.success) {
        setComments(Array.isArray(results[1].data) ? results[1].data : []);
      }
      if (results[2]?.success) {
        setDocuments(Array.isArray(results[2].data) ? results[2].data : []);
      }

      if (isAdmin) {
        if (results[3]?.success) {
          setCorrections(Array.isArray(results[3].data) ? results[3].data : []);
        }
        if (results[4]?.success) {
          setEconomics(results[4].data || null);
        }
      }

      setSecondaryLoading(false);
    },
    [actions, caseId, isAdmin],
  );

  const reload = useCallback(async () => {
    const controller = new AbortController();
    setError("");
    const primaryLoaded = await loadPrimary(controller.signal);

    if (primaryLoaded) {
      await loadSecondary(controller.signal);
    }
  }, [loadPrimary, loadSecondary]);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError("");
      const primaryLoaded = await loadPrimary(controller.signal);

      if (primaryLoaded) {
        await loadSecondary(controller.signal);
      }

      if (!controller.signal.aborted) {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [loadPrimary, loadSecondary]);

  useEffect(
    () => () => {
      if (documentPreview?.objectUrl) {
        window.URL.revokeObjectURL(documentPreview.objectUrl);
      }
    },
    [documentPreview?.objectUrl],
  );

  const currentStatus = repairCase?.status;
  const isDeleted = Boolean(repairCase?.is_deleted);
  const isTerminal = ["CERRADO", "ANULADO"].includes(currentStatus);
  const canEdit =
    !isDeleted && currentStatus === "BORRADOR" && ["admin", "tecnico"].includes(role);
  const visibleAllowedTransitions = isDeleted ? [] : allowedTransitions;

  useEffect(() => {
    if (!isDeleted) return;

    setEditing(false);
    setTransitionOpen(false);
    setResponsibleOpen(false);
    setCorrectionOpen(false);
    setDocumentToDelete(null);
    setDeleteReason("");
    setActionError("");
  }, [isDeleted]);

  const documentTypes = useMemo(() => {
    const values = options?.document_types || Object.keys(REPAIR_DOCUMENT_TYPE_LABELS);

    if (isAdmin) return values;

    return values.filter((value) =>
      ["DIAGNOSTICO", "INFORME_REPARACION", "FOTOGRAFIA", "OTRO"].includes(value),
    );
  }, [isAdmin, options]);

  const commentCategories = useMemo(() => {
    const values =
      options?.comment_categories || Object.keys(REPAIR_COMMENT_CATEGORY_LABELS);

    if (isAdmin) return values;

    return values.filter((value) =>
      ["TECNICO", "SEGUIMIENTO", "RECEPCION", "OTRO"].includes(value),
    );
  }, [isAdmin, options]);

  const closeResults =
    options?.close_results || Object.keys(REPAIR_CLOSE_RESULT_LABELS);

  const openTransition = (target = "") => {
    if (isDeleted) return;

    setActionError("");
    setTransitionForm({
      new_status: target,
      comment: "",
      sent_at: repairCase?.shipment?.sent_at?.slice(0, 10) || "",
      estimated_return_date:
        repairCase?.shipment?.estimated_return_date?.slice(0, 10) || "",
      received_at: repairCase?.shipment?.received_at?.slice(0, 10) || "",
      close_result: "",
      close_reason: "",
      cancellation_reason: "",
    });
    setTransitionOpen(true);
  };

  const submitTransition = async () => {
    if (isDeleted) return;

    setActionError("");

    if (!transitionForm.new_status) {
      setActionError("Selecciona el nuevo estado");
      return;
    }

    if (
      transitionForm.new_status === "CERRADO" &&
      (!transitionForm.close_result || !transitionForm.close_reason.trim())
    ) {
      setActionError("El cierre requiere resultado y motivo");
      return;
    }

    if (
      transitionForm.new_status === "ANULADO" &&
      !transitionForm.cancellation_reason.trim()
    ) {
      setActionError("La anulación requiere un motivo");
      return;
    }

    setActionLoading(true);

    const payload = Object.fromEntries(
      Object.entries(transitionForm).map(([key, value]) => [
        key,
        typeof value === "string" && !value.trim() ? null : value,
      ]),
    );

    const result = await actions.transitionRepairableSparePart(caseId, payload);
    setActionLoading(false);

    if (!result.success) {
      setActionError(result.error);
      return;
    }

    setTransitionOpen(false);
    await reload();
  };

  const submitResponsible = async () => {
    if (isDeleted) return;

    setActionError("");
    setActionLoading(true);

    const result = await actions.updateRepairableSparePartResponsible(caseId, {
      responsible_user_id:
        responsibleForm.responsible_user_id === ""
          ? null
          : Number(responsibleForm.responsible_user_id),
      reason: responsibleForm.reason.trim() || null,
    });

    setActionLoading(false);

    if (!result.success) {
      setActionError(result.error);
      return;
    }

    setResponsibleOpen(false);
    await reload();
  };

  const submitCorrection = async () => {
    if (isDeleted) return;

    if (
      !correctionForm.reason.trim() ||
      !correctionForm.field_name ||
      !correctionForm.new_value.trim()
    ) {
      setActionError("La corrección requiere campo, valor y motivo");
      return;
    }

    setActionLoading(true);
    setActionError("");

    const result = await actions.createRepairableSparePartCorrection(caseId, {
      reason: correctionForm.reason.trim(),
      changes: {
        [correctionForm.field_name]: correctionForm.new_value.trim(),
      },
    });

    setActionLoading(false);

    if (!result.success) {
      setActionError(result.error);
      return;
    }

    setCorrectionOpen(false);
    await reload();
  };

  const submitComment = async (event) => {
    event.preventDefault();

    if (isDeleted || !commentForm.text.trim()) return;

    setCommentLoading(true);

    const result = await actions.createRepairableSparePartComment(caseId, {
      ...commentForm,
      text: commentForm.text.trim(),
    });

    setCommentLoading(false);

    if (!result.success) {
      actions.showToast(result.error, "error");
      return;
    }

    setCommentForm((current) => ({
      ...current,
      text: "",
    }));

    const refreshed = await actions.getRepairableSparePartComments(caseId);
    if (refreshed.success) {
      setComments(Array.isArray(refreshed.data) ? refreshed.data : []);
    }
  };

  const submitDocument = async (event) => {
    event.preventDefault();

    if (isDeleted) return;

    if (!documentForm.file) {
      actions.showToast("Selecciona un archivo", "error");
      return;
    }

    const formData = new FormData();
    formData.append("file", documentForm.file);
    formData.append("document_type", documentForm.document_type);
    formData.append("document_scope", isAdmin ? documentForm.document_scope : "TECNICO");

    if (documentForm.description.trim()) {
      formData.append("description", documentForm.description.trim());
    }

    setDocumentLoading(true);
    const result = await actions.uploadRepairableSparePartDocument(caseId, formData);
    setDocumentLoading(false);

    if (!result.success) {
      actions.showToast(result.error, "error");
      return;
    }

    setDocumentForm({
      file: null,
      document_type: isAdmin ? "PRESUPUESTO" : "DIAGNOSTICO",
      document_scope: isAdmin ? "ADMINISTRATIVO" : "TECNICO",
      description: "",
    });

    const fileInput = document.getElementById("repairable_document_file");
    if (fileInput) fileInput.value = "";

    const refreshed = await actions.getRepairableSparePartDocuments(caseId);
    if (refreshed.success) {
      setDocuments(Array.isArray(refreshed.data) ? refreshed.data : []);
    }
  };

  const previewDocument = async (documentItem) => {
    const result = await actions.previewRepairableSparePartDocument(
      caseId,
      documentItem.id,
    );

    if (!result.success) {
      actions.showToast(result.error, "error");
      return;
    }

    if (documentPreview?.objectUrl) {
      window.URL.revokeObjectURL(documentPreview.objectUrl);
    }

    setDocumentPreview({
      ...result.data,
      document: documentItem,
    });
  };

  const deleteDocument = async () => {
    if (
      isDeleted ||
      !documentToDelete ||
      !deleteReason.trim()
    ) {
      return;
    }

    setDocumentLoading(true);
    const result = await actions.deleteRepairableSparePartDocument(
      caseId,
      documentToDelete.id,
      deleteReason.trim(),
    );
    setDocumentLoading(false);

    if (!result.success) {
      actions.showToast(result.error, "error");
      return;
    }

    setDocumentToDelete(null);
    setDeleteReason("");

    const refreshed = await actions.getRepairableSparePartDocuments(
      caseId,
      { include_deleted: true },
    );
    if (refreshed.success) {
      setDocuments(Array.isArray(refreshed.data) ? refreshed.data : []);
    }
  };

  if (loading) {
    return (
      <main className="container mt-5 repairable-page">
        <div className="repairable-state">Cargando expediente…</div>
      </main>
    );
  }

  if (error || !repairCase) {
    return (
      <main className="container mt-5 repairable-page">
        <div className="repairable-alert repairable-alert-error">
          {error || "No se encontró el expediente"}
        </div>
        <Link className="btn btn-outline-secondary mt-3" to="/reparable-spare-parts">
          Volver al listado
        </Link>
      </main>
    );
  }

  const material = repairCase.material_snapshot || {};
  const supplier = repairCase.supplier || {};
  const technical = repairCase.technical || {};
  const shipment = repairCase.shipment || {};
  const warranty = repairCase.warranty || {};
  const incident = repairCase.incident || {};
  const closure = repairCase.closure || {};
  const responsibility = repairCase.responsibility || {};
  const relation = repairCase.relation || {};

  return (
    <main className="container-fluid mt-4 repairable-page repairable-detail-page">
      <header className="repairable-page-header repairable-detail-header">
        <div>
          <Link className="repairable-back-link" to={isDeleted ? "/reparable-spare-parts/deleted" : "/reparable-spare-parts"}>
            <i className="bi bi-arrow-left"></i>
            {isDeleted ? "Historial de eliminados" : "Repuestos en reparación"}
          </Link>
          <div className="repairable-title-row">
            <div>
              <span className="repairable-eyebrow">EXPEDIENTE</span>
              <h1>{repairCase.case_number}</h1>
              <p>
                {material.material_id} · {material.description || "Sin descripción"}
              </p>
            </div>
            <span className={statusClass(repairCase.status)}>
              {REPAIR_STATUS_LABELS[repairCase.status] || repairCase.status}
            </span>
          </div>
        </div>

        <div className="repairable-header-actions">
          {canEdit && (
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => setEditing((current) => !current)}
            >
              <i className="bi bi-pencil me-2"></i>
              {editing ? "Cerrar edición" : "Editar borrador"}
            </button>
          )}

          {isAdmin && !isTerminal && !isDeleted && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                setResponsibleForm({
                  responsible_user_id: responsibility.responsible_user?.id
                    ? String(responsibility.responsible_user.id)
                    : "",
                  reason: "",
                });
                setActionError("");
                setResponsibleOpen(true);
              }}
            >
              <i className="bi bi-person-gear me-2"></i>
              Responsable
            </button>
          )}

          {visibleAllowedTransitions.length > 0 && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => openTransition(visibleAllowedTransitions[0])}
            >
              <i className="bi bi-arrow-right-circle me-2"></i>
              Cambiar estado
            </button>
          )}
        </div>
      </header>

      {isDeleted && (
        <div className="repairable-deleted-banner">
          <strong>Expediente eliminado</strong>
          <span>Fecha de eliminación: {formatDate(repairCase.deletion?.deleted_at, true)}</span>
          <span>Usuario que eliminó: {userName(repairCase.deletion?.deleted_by)}</span>
          <span>Motivo: {repairCase.deletion?.reason || "—"}</span>
          <span>Este expediente está disponible únicamente en modo de consulta.</span>
        </div>
      )}

      {repairCase.delayed && (
        <div className="repairable-alert repairable-alert-warning">
          <i className="bi bi-clock-history" aria-hidden="true"></i>
          El expediente ha superado la fecha prevista de retorno.
        </div>
      )}

      {warnings.map((warning, index) => (
        <div
          className="repairable-alert repairable-alert-warning"
          key={`${warning?.code || "warning"}-${index}`}
        >
          {formatCaseWarning(warning)}
        </div>
      ))}

      {editing && !isDeleted && (
        <section className="repairable-edit-panel">
          <RepairableSparePartForm
            embedded
            initialCase={repairCase}
            economics={economics}
            options={options}
            onCancel={() => setEditing(false)}
            onSuccess={async () => {
              setEditing(false);
              await reload();
            }}
          />
        </section>
      )}

      <section className="repairable-detail-layout">
        <div className="repairable-detail-main">
          <section className="repairable-card repairable-detail-section">
            <div className="repairable-section-heading">
              <div>
                <span className="repairable-eyebrow">MATERIAL</span>
                <h2>Identificación ORMA</h2>
              </div>
              <Link
                className="btn btn-outline-primary btn-sm"
                to={`/orma-catalog?search=${encodeURIComponent(material.material_id || "")}`}
              >
                Ver en ORMA
              </Link>
            </div>
            <div className="repairable-detail-grid">
              <DetailItem label="ID material" value={material.material_id} />
              <DetailItem label="ID AX" value={material.id_material_ax} />
              <DetailItem label="Descripción" value={material.description} wide />
              <DetailItem label="Marca" value={material.brand} />
              <DetailItem label="Modelo" value={material.model} />
              <DetailItem label="Ref. fabricante" value={material.manufacturer_ref} />
              <DetailItem label="Ref. suministrador" value={material.supplier_ref} />
              <DetailItem label="Alias" value={material.alias} />
              <DetailItem label="Cantidad" value={repairCase.quantity} />
              <DetailItem label="Número de serie" value={repairCase.serial_number} />
              <DetailItem label="Equipo" value={repairCase.equipment_code} />
            </div>
          </section>

          <section className="repairable-card repairable-detail-section">
            <div className="repairable-section-heading">
              <div>
                <span className="repairable-eyebrow">AVERÍA Y REPARACIÓN</span>
                <h2>Información técnica</h2>
              </div>
            </div>
            <div className="repairable-detail-grid">
              <DetailItem
                label="Avería o motivo"
                value={technical.failure_description}
                wide
              />
              <DetailItem
                label="Trabajo solicitado"
                value={technical.requested_work}
                wide
              />
              <DetailItem
                label="Diagnóstico del proveedor"
                value={technical.supplier_diagnosis}
                wide
              />
              <DetailItem
                label="Reparación realizada"
                value={technical.performed_repair}
                wide
              />
              <DetailItem
                label="Resultado de recepción"
                value={technical.reception_result}
                wide
              />
              <DetailItem label="Notas finales" value={technical.final_notes} wide />
            </div>
          </section>

          <section className="repairable-card repairable-detail-section">
            <div className="repairable-section-heading">
              <div>
                <span className="repairable-eyebrow">PROVEEDOR Y LOGÍSTICA</span>
                <h2>Envío y recepción</h2>
              </div>
            </div>
            <div className="repairable-detail-grid">
              <DetailItem label="Proveedor" value={supplier.name} wide />
              <DetailItem label="ID proveedor" value={supplier.id} />
              <DetailItem label="Origen" value={supplier.source} />
              <DetailItem label="Contacto" value={supplier.contact_name} />
              <DetailItem label="Teléfono" value={supplier.phone} />
              <DetailItem label="Correo" value={supplier.email} />
              <DetailItem label="Fecha de envío" value={formatDate(shipment.sent_at)} />
              <DetailItem
                label="Retorno previsto"
                value={formatDate(shipment.estimated_return_date)}
              />
              <DetailItem
                label="Fecha de recepción"
                value={formatDate(shipment.received_at)}
              />
              <DetailItem
                label="Albarán de salida"
                value={shipment.delivery_note_number}
              />
              <DetailItem label="Transportista" value={shipment.transport_company} />
              <DetailItem label="Seguimiento" value={shipment.tracking_number} />
              <DetailItem label="Contacto envío" value={shipment.shipment_contact} />
              <DetailItem label="Notas envío" value={shipment.shipment_notes} wide />
            </div>
          </section>

          {isAdmin && (
            <section className="repairable-card repairable-detail-section repairable-economic-card">
              <div className="repairable-section-heading">
                <div>
                  <span className="repairable-eyebrow">ECONOMÍA · ADMIN</span>
                  <h2>Presupuesto y pedido</h2>
                </div>
              </div>
              <div className="repairable-detail-grid">
                <DetailItem label="Presupuesto" value={economics?.quote_number} />
                <DetailItem
                  label="Fecha presupuesto"
                  value={formatDate(economics?.quote_received_at)}
                />
                <DetailItem
                  label="Importe presupuestado"
                  value={formatMoney(
                    economics?.quoted_amount,
                    economics?.currency,
                  )}
                />
                <DetailItem label="Pedido" value={economics?.purchase_order_number} />
                <DetailItem
                  label="Origen pedido"
                  value={economics?.purchase_order_source}
                />
                <DetailItem
                  label="Importe final"
                  value={formatMoney(economics?.final_amount, economics?.currency)}
                />
              </div>
            </section>
          )}

          <section className="repairable-card repairable-detail-section">
            <div className="repairable-section-heading">
              <div>
                <span className="repairable-eyebrow">COMENTARIOS</span>
                <h2>Seguimiento operativo</h2>
              </div>
            </div>

            {!isDeleted && (
              <form className="repairable-comment-form" onSubmit={submitComment}>
              <select
                value={commentForm.category}
                onChange={(event) =>
                  setCommentForm((current) => ({
                    ...current,
                    category: event.target.value,
                  }))
                }
              >
                {commentCategories.map((category) => (
                  <option key={category} value={category}>
                    {REPAIR_COMMENT_CATEGORY_LABELS[category] ||
                      category.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
              <textarea
                rows="3"
                value={commentForm.text}
                onChange={(event) =>
                  setCommentForm((current) => ({
                    ...current,
                    text: event.target.value,
                  }))
                }
                placeholder="Añadir comentario de seguimiento"
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={commentLoading || !commentForm.text.trim()}
              >
                {commentLoading ? "Añadiendo…" : "Añadir comentario"}
              </button>
              </form>
            )}

            <div className="repairable-comment-list">
              {comments.length === 0 && (
                <div className="repairable-state repairable-state-empty">
                  No hay comentarios registrados.
                </div>
              )}
              {comments.map((comment) => (
                <article className="repairable-comment" key={comment.id}>
                  <header>
                    <span>
                      {REPAIR_COMMENT_CATEGORY_LABELS[comment.category] ||
                        comment.category}
                    </span>
                    <time>{formatDate(comment.created_at, true)}</time>
                  </header>
                  <p>{comment.text}</p>
                  <small>{userName(comment.created_by)}</small>
                </article>
              ))}
            </div>
          </section>

          <section className="repairable-card repairable-detail-section">
            <div className="repairable-section-heading">
              <div>
                <span className="repairable-eyebrow">DOCUMENTOS</span>
                <h2>Archivo del expediente</h2>
              </div>
            </div>

            {!isDeleted && (
              <form
                className="repairable-document-form"
                onSubmit={submitDocument}
              >
              <div className="repairable-field">
                <label htmlFor="repairable_document_file">Archivo</label>
                <input
                  id="repairable_document_file"
                  type="file"
                  accept=".pdf,.xlsx,.xls,.docx,.doc,.jpg,.jpeg,.png"
                  onChange={(event) =>
                    setDocumentForm((current) => ({
                      ...current,
                      file: event.target.files?.[0] || null,
                    }))
                  }
                />
              </div>
              <div className="repairable-field">
                <label htmlFor="repairable_document_type">Tipo</label>
                <select
                  id="repairable_document_type"
                  value={documentForm.document_type}
                  onChange={(event) =>
                    setDocumentForm((current) => ({
                      ...current,
                      document_type: event.target.value,
                    }))
                  }
                >
                  {documentTypes.map((type) => (
                    <option key={type} value={type}>
                      {REPAIR_DOCUMENT_TYPE_LABELS[type] ||
                        type.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
              {isAdmin && (
                <div className="repairable-field">
                  <label htmlFor="repairable_document_scope">Ámbito</label>
                  <select
                    id="repairable_document_scope"
                    value={documentForm.document_scope}
                    onChange={(event) =>
                      setDocumentForm((current) => ({
                        ...current,
                        document_scope: event.target.value,
                      }))
                    }
                  >
                    {(options?.document_scopes || ["TECNICO", "ADMINISTRATIVO"]).map(
                      (scope) => (
                        <option key={scope} value={scope}>
                          {scope.replaceAll("_", " ")}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              )}
              <div className="repairable-field repairable-field-span-2">
                <label htmlFor="repairable_document_description">Descripción</label>
                <input
                  id="repairable_document_description"
                  value={documentForm.description}
                  onChange={(event) =>
                    setDocumentForm((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                />
              </div>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={documentLoading || !documentForm.file}
              >
                {documentLoading ? "Procesando…" : "Subir documento"}
              </button>
              </form>
            )}

            <div className="repairable-document-list">
              {documents.filter((documentItem) => !documentItem.is_deleted).length ===
                0 && (
                <div className="repairable-state repairable-state-empty">
                  No hay documentos disponibles.
                </div>
              )}

              {documents.map((documentItem) => (
                <article
                  className={`repairable-document${
                    documentItem.is_deleted ? " repairable-document-deleted" : ""
                  }`}
                  key={documentItem.id}
                >
                  <div className="repairable-document-icon">
                    <i className="bi bi-file-earmark-text"></i>
                  </div>
                  <div className="repairable-document-copy">
                    <strong>{documentItem.original_filename}</strong>
                    <span>
                      {REPAIR_DOCUMENT_TYPE_LABELS[documentItem.document_type] ||
                        documentItem.document_type}{" "}
                      · {documentItem.document_scope} ·{" "}
                      {formatFileSize(documentItem.file_size)}
                    </span>
                    <small>
                      {formatDate(documentItem.uploaded_at, true)} ·{" "}
                      {userName(documentItem.uploaded_by)}
                    </small>
                    {documentItem.description && <p>{documentItem.description}</p>}
                    {documentItem.is_deleted && (
                      <em>
                        Eliminado lógicamente el{" "}
                        {formatDate(documentItem.deleted_at, true)} ·{" "}
                        {userName(documentItem.deleted_by)} ·{" "}
                        {documentItem.deletion_reason || "sin motivo"}
                      </em>
                    )}
                  </div>
                  {!documentItem.is_deleted && (
                    <div className="repairable-document-actions">
                      {canPreviewDocument(documentItem) && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => previewDocument(documentItem)}
                        >
                          Vista previa
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={async () => {
                          const result =
                            await actions.downloadRepairableSparePartDocument(
                              caseId,
                              documentItem.id,
                              documentItem.original_filename,
                            );
                          if (!result.success) {
                            actions.showToast(result.error, "error");
                          }
                        }}
                      >
                        Descargar
                      </button>
                      {isAdmin && !isDeleted && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => {
                            setDocumentToDelete(documentItem);
                            setDeleteReason("");
                          }}
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        </div>

        <aside className="repairable-detail-aside">
          <section className="repairable-card repairable-side-card">
            <span className="repairable-eyebrow">CONTROL OPERATIVO</span>
            <div className="repairable-side-metric">
              <span>Días fuera</span>
              <strong>{displayValue(repairCase.days_out)}</strong>
            </div>
            <div className="repairable-side-metric">
              <span>Retorno previsto</span>
              <strong>{formatDate(shipment.estimated_return_date)}</strong>
            </div>
            <div className="repairable-side-metric">
              <span>Responsable</span>
              <strong>{userName(responsibility.responsible_user)}</strong>
            </div>
          </section>

          <section className="repairable-card repairable-side-card">
            <span className="repairable-eyebrow">ACCIONES</span>
            <div className="repairable-side-actions">
              {allowedTransitions.map((target) => (
                <button
                  key={target}
                  type="button"
                  className={
                    target === "ANULADO" || target === "CERRADO"
                      ? "btn btn-outline-danger"
                      : "btn btn-outline-primary"
                  }
                  onClick={() => openTransition(target)}
                >
                  {REPAIR_STATUS_LABELS[target] || target}
                </button>
              ))}

              {isAdmin && !isTerminal && !isDeleted && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setActionError("");
                    setCorrectionOpen(true);
                  }}
                >
                  Registrar corrección
                </button>
              )}
            </div>
          </section>

          <section className="repairable-card repairable-side-card">
            <span className="repairable-eyebrow">GARANTÍA Y RELACIÓN</span>
            <div className="repairable-compact-list">
              <div>
                <span>En garantía</span>
                <strong>{warranty.is_warranty ? "Sí" : "No"}</strong>
              </div>
              <div>
                <span>Referencia</span>
                <strong>{displayValue(warranty.reference)}</strong>
              </div>
              <div>
                <span>Vencimiento</span>
                <strong>{formatDate(warranty.expires_at)}</strong>
              </div>
              <div>
                <span>Expediente anterior</span>
                <strong>{displayValue(relation.previous_case_number)}</strong>
              </div>
              <div>
                <span>Relación</span>
                <strong>{displayValue(relation.type)}</strong>
              </div>
            </div>
          </section>

          {repairCase.status === "INCIDENCIA" || incident.description ? (
            <section className="repairable-card repairable-side-card repairable-incident-card">
              <span className="repairable-eyebrow">INCIDENCIA</span>
              <div className="repairable-compact-list">
                <div>
                  <span>Descripción</span>
                  <strong>{displayValue(incident.description)}</strong>
                </div>
                <div>
                  <span>Registrada</span>
                  <strong>{formatDate(incident.opened_at, true)}</strong>
                </div>
                <div>
                  <span>Registrada por</span>
                  <strong>{userName(incident.opened_by)}</strong>
                </div>
              </div>
            </section>
          ) : null}

          {isTerminal && (
            <section className="repairable-card repairable-side-card">
              <span className="repairable-eyebrow">CIERRE</span>
              <div className="repairable-compact-list">
                <div>
                  <span>Resultado</span>
                  <strong>
                    {REPAIR_CLOSE_RESULT_LABELS[closure.close_result] ||
                      displayValue(closure.close_result)}
                  </strong>
                </div>
                <div>
                  <span>Motivo</span>
                  <strong>
                    {displayValue(
                      closure.close_reason || closure.cancellation_reason,
                    )}
                  </strong>
                </div>
                <div>
                  <span>Fecha</span>
                  <strong>
                    {formatDate(closure.closed_at || closure.cancelled_at, true)}
                  </strong>
                </div>
              </div>
            </section>
          )}

          <section className="repairable-card repairable-side-card">
            <div className="repairable-section-heading">
              <div>
                <span className="repairable-eyebrow">HISTORIAL</span>
                <h2>Trazabilidad</h2>
              </div>
            </div>
            {secondaryLoading && history.length === 0 ? (
              <div className="repairable-inline-state">Cargando…</div>
            ) : (
              <div className="repairable-timeline">
                {history.length === 0 && (
                  <div className="repairable-state repairable-state-empty">
                    Sin eventos.
                  </div>
                )}
                {history.map((event) => (
                  <article className="repairable-timeline-item" key={event.id}>
                    <span className="repairable-timeline-dot"></span>
                    <div>
                      <strong>
                        {event.new_status
                          ? REPAIR_STATUS_LABELS[event.new_status] ||
                            event.new_status
                          : event.event_type}
                      </strong>
                      <time>{formatDate(event.created_at, true)}</time>
                      {(event.comment || event.reason) && (
                        <p>{event.comment || event.reason}</p>
                      )}
                      <small>{userName(event.performed_by)}</small>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          {isAdmin && corrections.length > 0 && (
            <section className="repairable-card repairable-side-card">
              <span className="repairable-eyebrow">CORRECCIONES</span>
              <div className="repairable-correction-list">
                {corrections.map((correction) => (
                  <article key={correction.id}>
                    <strong>{correction.field_name}</strong>
                    <span>
                      {displayValue(correction.old_value)} →{" "}
                      {displayValue(correction.new_value)}
                    </span>
                    <small>
                      {formatDate(correction.created_at, true)} ·{" "}
                      {userName(correction.performed_by)}
                    </small>
                    <p>{correction.reason}</p>
                  </article>
                ))}
              </div>
            </section>
          )}
        </aside>
      </section>

      <Modal
        open={!isDeleted && transitionOpen}
        title="Cambiar estado"
        onClose={() => setTransitionOpen(false)}
        danger={["CERRADO", "ANULADO"].includes(transitionForm.new_status)}
        footer={
          <>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setTransitionOpen(false)}
              disabled={actionLoading}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={
                ["CERRADO", "ANULADO"].includes(transitionForm.new_status)
                  ? "btn btn-danger"
                  : "btn btn-primary"
              }
              onClick={submitTransition}
              disabled={actionLoading}
            >
              {actionLoading ? "Aplicando…" : "Confirmar transición"}
            </button>
          </>
        }
      >
        <div className="repairable-form-grid">
          <div className="repairable-field repairable-field-span-2">
            <label htmlFor="transition_status">Nuevo estado</label>
            <select
              id="transition_status"
              value={transitionForm.new_status}
              onChange={(event) =>
                setTransitionForm((current) => ({
                  ...current,
                  new_status: event.target.value,
                }))
              }
            >
              <option value="">Seleccionar</option>
              {allowedTransitions.map((target) => (
                <option key={target} value={target}>
                  {REPAIR_STATUS_LABELS[target] || target}
                </option>
              ))}
            </select>
          </div>

          {transitionForm.new_status === "ENVIADO" && (
            <>
              <div className="repairable-field">
                <label htmlFor="transition_sent_at">Fecha de envío</label>
                <input
                  id="transition_sent_at"
                  type="date"
                  value={transitionForm.sent_at}
                  onChange={(event) =>
                    setTransitionForm((current) => ({
                      ...current,
                      sent_at: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="repairable-field">
                <label htmlFor="transition_estimated_return">
                  Retorno previsto
                </label>
                <input
                  id="transition_estimated_return"
                  type="date"
                  value={transitionForm.estimated_return_date}
                  onChange={(event) =>
                    setTransitionForm((current) => ({
                      ...current,
                      estimated_return_date: event.target.value,
                    }))
                  }
                />
              </div>
            </>
          )}

          {transitionForm.new_status === "RECIBIDO" && (
            <div className="repairable-field repairable-field-span-2">
              <label htmlFor="transition_received_at">Fecha de recepción</label>
              <input
                id="transition_received_at"
                type="date"
                value={transitionForm.received_at}
                onChange={(event) =>
                  setTransitionForm((current) => ({
                    ...current,
                    received_at: event.target.value,
                  }))
                }
              />
            </div>
          )}

          {transitionForm.new_status === "CERRADO" && (
            <>
              <div className="repairable-field repairable-field-span-2">
                <label htmlFor="transition_close_result">Resultado del cierre</label>
                <select
                  id="transition_close_result"
                  value={transitionForm.close_result}
                  onChange={(event) =>
                    setTransitionForm((current) => ({
                      ...current,
                      close_result: event.target.value,
                    }))
                  }
                >
                  <option value="">Seleccionar</option>
                  {closeResults.map((result) => (
                    <option key={result} value={result}>
                      {REPAIR_CLOSE_RESULT_LABELS[result] ||
                        result.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div className="repairable-field repairable-field-span-2">
                <label htmlFor="transition_close_reason">Motivo del cierre</label>
                <textarea
                  id="transition_close_reason"
                  rows="3"
                  value={transitionForm.close_reason}
                  onChange={(event) =>
                    setTransitionForm((current) => ({
                      ...current,
                      close_reason: event.target.value,
                    }))
                  }
                />
              </div>
            </>
          )}

          {transitionForm.new_status === "ANULADO" && (
            <div className="repairable-field repairable-field-span-2">
              <label htmlFor="transition_cancellation_reason">
                Motivo de anulación
              </label>
              <textarea
                id="transition_cancellation_reason"
                rows="3"
                value={transitionForm.cancellation_reason}
                onChange={(event) =>
                  setTransitionForm((current) => ({
                    ...current,
                    cancellation_reason: event.target.value,
                  }))
                }
              />
            </div>
          )}

          <div className="repairable-field repairable-field-span-2">
            <label htmlFor="transition_comment">Comentario</label>
            <textarea
              id="transition_comment"
              rows="3"
              value={transitionForm.comment}
              onChange={(event) =>
                setTransitionForm((current) => ({
                  ...current,
                  comment: event.target.value,
                }))
              }
            />
          </div>
        </div>
        {actionError && (
          <div className="repairable-alert repairable-alert-error">{actionError}</div>
        )}
      </Modal>

      <Modal
        open={!isDeleted && responsibleOpen}
        title="Cambiar responsable"
        onClose={() => setResponsibleOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setResponsibleOpen(false)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={submitResponsible}
              disabled={actionLoading}
            >
              Guardar
            </button>
          </>
        }
      >
        <div className="repairable-form-grid">
          <div className="repairable-field repairable-field-span-2">
            <label htmlFor="responsible_user_id">ID de usuario</label>
            <input
              id="responsible_user_id"
              type="number"
              min="1"
              value={responsibleForm.responsible_user_id}
              onChange={(event) =>
                setResponsibleForm((current) => ({
                  ...current,
                  responsible_user_id: event.target.value,
                }))
              }
              placeholder="Vacío para retirar la asignación"
            />
          </div>
          <div className="repairable-field repairable-field-span-2">
            <label htmlFor="responsible_reason">Motivo</label>
            <textarea
              id="responsible_reason"
              rows="3"
              value={responsibleForm.reason}
              onChange={(event) =>
                setResponsibleForm((current) => ({
                  ...current,
                  reason: event.target.value,
                }))
              }
            />
          </div>
        </div>
        {actionError && (
          <div className="repairable-alert repairable-alert-error">{actionError}</div>
        )}
      </Modal>

      <Modal
        open={!isDeleted && correctionOpen}
        title="Registrar corrección"
        onClose={() => setCorrectionOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setCorrectionOpen(false)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={submitCorrection}
              disabled={actionLoading}
            >
              Registrar
            </button>
          </>
        }
      >
        <div className="repairable-form-grid">
          <div className="repairable-field repairable-field-span-2">
            <label htmlFor="correction_field">Campo</label>
            <select
              id="correction_field"
              value={correctionForm.field_name}
              onChange={(event) =>
                setCorrectionForm((current) => ({
                  ...current,
                  field_name: event.target.value,
                }))
              }
            >
              {[
                ["final_notes", "Notas finales"],
                ["supplier_diagnosis", "Diagnóstico del proveedor"],
                ["performed_repair", "Reparación realizada"],
                ["reception_result", "Resultado de recepción"],
                ["shipment_notes", "Notas del envío"],
                ["supplier_name", "Nombre del proveedor"],
                ["quote_number", "Número de presupuesto"],
                ["purchase_order_number", "Número de pedido"],
              ].map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="repairable-field repairable-field-span-2">
            <label htmlFor="correction_value">Nuevo valor</label>
            <textarea
              id="correction_value"
              rows="3"
              value={correctionForm.new_value}
              onChange={(event) =>
                setCorrectionForm((current) => ({
                  ...current,
                  new_value: event.target.value,
                }))
              }
            />
          </div>
          <div className="repairable-field repairable-field-span-2">
            <label htmlFor="correction_reason">Motivo</label>
            <textarea
              id="correction_reason"
              rows="3"
              value={correctionForm.reason}
              onChange={(event) =>
                setCorrectionForm((current) => ({
                  ...current,
                  reason: event.target.value,
                }))
              }
            />
          </div>
        </div>
        {actionError && (
          <div className="repairable-alert repairable-alert-error">{actionError}</div>
        )}
      </Modal>

      <Modal
        open={Boolean(documentPreview)}
        title={documentPreview?.document?.original_filename || "Vista previa"}
        onClose={() => {
          if (documentPreview?.objectUrl) {
            window.URL.revokeObjectURL(documentPreview.objectUrl);
          }
          setDocumentPreview(null);
        }}
        footer={
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => {
              if (documentPreview?.objectUrl) {
                window.URL.revokeObjectURL(documentPreview.objectUrl);
              }
              setDocumentPreview(null);
            }}
          >
            Cerrar
          </button>
        }
      >
        {documentPreview?.contentType?.includes("pdf") ? (
          <iframe
            className="repairable-preview-frame"
            src={documentPreview.objectUrl}
            title="Vista previa PDF"
          />
        ) : documentPreview?.contentType?.startsWith("image/") ? (
          <img
            className="repairable-preview-image"
            src={documentPreview.objectUrl}
            alt={documentPreview.document?.original_filename || "Vista previa"}
          />
        ) : (
          <div className="repairable-state">
            Este formato no admite vista previa integrada.
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!isDeleted && Boolean(documentToDelete)}
        title="Eliminar documento"
        message={`El documento ${
          documentToDelete?.original_filename || "seleccionado"
        } quedará eliminado lógicamente. El archivo físico se conservará para trazabilidad.`}
        confirmLabel={documentLoading ? "Eliminando…" : "Eliminar"}
        cancelLabel="Cancelar"
        danger
        onConfirm={() => {
          if (!deleteReason.trim()) {
            actions.showToast("Indica el motivo de eliminación", "error");
            return;
          }
          deleteDocument();
        }}
        onCancel={() => {
          setDocumentToDelete(null);
          setDeleteReason("");
        }}
      />

      {!isDeleted && documentToDelete && (
        <div className="repairable-delete-reason">
          <label htmlFor="delete_document_reason">Motivo de eliminación</label>
          <input
            id="delete_document_reason"
            value={deleteReason}
            onChange={(event) => setDeleteReason(event.target.value)}
            placeholder="Motivo obligatorio"
          />
        </div>
      )}
    </main>
  );
};

export default RepairableSparePartDetail;
